import React from 'react';
import { Typography, Box, Container, Paper, Button, Divider, Chip, Rating } from '@mui/material';
import { 
  AccountBalance, 
  Assessment, 
  Security, 
  TrendingUp, 
  ReceiptLong, 
  BusinessCenter,
  Groups,
  CheckCircle,
  Speed,
  EmojiEvents,
  Phone
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { HEADER_HEIGHT } from '../App';
import { trackPhoneClick } from '../utils/gtag';
import ScrollDownButton from '../components/ScrollDownButton';

import Grid from '@mui/material/GridLegacy';
import { usePageMeta } from '../hooks/usePageMeta';


// Real Google review (12 Jun 2025). Add more here as clients leave written ones.
const GOOGLE_PROFILE_URL = "https://g.page/r/CQcWJDszhD0SEAE";
const GOOGLE_WRITE_REVIEW_URL = "https://g.page/r/CQcWJDszhD0SEAE/review";
const featuredReview = {
  name: "Genie Group",
  source: "Google review",
  review: "Top class tax agent, good honest people that go the extra mile to provide genuine personal service, which is rarer than rare.. highly recommended!",
  rating: 5
};

const services = [
  {
    icon: <AccountBalance sx={{ fontSize: 48, mb: 2, color: 'secondary.main' }} />,
    title: "Tax Returns & Planning",
    description: "Individual and business tax returns prepared properly, with practical advice through the year on how to structure things.",
  },
  {
    icon: <Assessment sx={{ fontSize: 48, mb: 2, color: 'secondary.main' }} />,
    title: "Bookkeeping, GST & BAS",
    description: "Accurate books, BAS lodged on time, and payroll handled, so you always know where you stand with the ATO.",
  },
  {
    icon: <Security sx={{ fontSize: 48, mb: 2, color: 'secondary.main' }} />,
    title: "Business Advisory",
    description: "Practical guidance on cash flow, business structure, and growth decisions from an accountant who takes the time to know your business.",
  }
];

const achievements = [
  {
    icon: <EmojiEvents sx={{ fontSize: 40, color: 'secondary.main' }} />,
    number: "15+",
    label: "Years in Sydney"
  },
  {
    icon: <Groups sx={{ fontSize: 40, color: 'secondary.main' }} />,
    number: "5.0",
    label: "Google Rating"
  },
  {
    icon: <Speed sx={{ fontSize: 40, color: 'secondary.main' }} />,
    number: "IPA",
    label: "Institute of Public Accountants Member"
  }
];

const Home: React.FC = () => {
  usePageMeta(
    'Good Shepherd Accounting | Tax Agent & Accountant in Padstow, Sydney',
    'Personalised tax, bookkeeping and business advisory from a Padstow-based accountant with 15 years of Sydney experience. IPA member. Book a free consultation.'
  );
  const scrollToContent = () => {
    const contentElement = document.getElementById('home-content');
    if (contentElement) {
      contentElement.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
      <Box
        sx={{
          minHeight: `calc(100vh - ${HEADER_HEIGHT}px)`,
          display: 'flex',
          alignItems: 'center',
          background: 'linear-gradient(135deg, #1B3C69 0%, #2B5999 100%)',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'url(/images/pattern.svg) repeat',
            opacity: 0.1,
            zIndex: 1,
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '4px',
            backgroundColor: 'secondary.main',
            zIndex: 2,
          },
        }}
      >
        <Container 
          maxWidth="lg" 
          sx={{ 
            position: 'relative',
            zIndex: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
          }}
        >
          <Chip 
            label="Good Shepherd Accountants" 
            sx={{ 
              backgroundColor: 'secondary.main', 
              color: 'white',
              mb: 3,
              fontSize: '0.9rem',
              fontWeight: 600,
              height: 32,
              padding: '16px',
            }} 
          />
          <Typography 
            variant="h1" 
            component="h1" 
            sx={{ 
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              mb: 3,
              fontWeight: 700,
              lineHeight: 1.2,
              maxWidth: 800,
              position: 'relative',
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: -16,
                left: '50%',
                transform: 'translateX(-50%)',
                width: 100,
                height: 4,
                backgroundColor: 'secondary.main',
              }
            }}
          >
            Your Trusted Financial Partner in Sydney
          </Typography>
          <Typography 
            variant="h5" 
            sx={{ 
              opacity: 0.9,
              fontWeight: 400,
              maxWidth: 600,
              mx: 'auto',
              fontSize: { xs: '1.1rem', md: '1.3rem' },
              lineHeight: 1.6,
              mb: 4,
            }}
          >
            Personalised accounting for Sydney businesses and individuals, backed by 15 years of local experience
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              color="secondary"
              size="large"
              href="tel:+61425660773"
              onClick={trackPhoneClick}
              startIcon={<Phone />}
            >
              Call 0425 660 773
            </Button>
            <Button
              variant="outlined"
              size="large"
              component={RouterLink}
              to="/contact"
              sx={{
                color: 'white',
                borderColor: 'rgba(255,255,255,0.7)',
                '&:hover': { borderColor: 'white', backgroundColor: 'rgba(255,255,255,0.08)' },
              }}
            >
              Book a Free Consultation
            </Button>
          </Box>
        </Container>
        <ScrollDownButton onClick={scrollToContent} />
      </Box>

      <Box id="home-content">
        {/* Achievements Section */}
        <Box
          sx={{
            minHeight: '50vh',
            display: 'flex',
            alignItems: 'center',
            py: 6,
            backgroundColor: '#f8f9fa',
            width: '100%',
          }}
        >
          <Container 
            maxWidth={false}
            sx={{
              px: { xs: 2, sm: 3, md: 6, lg: 8 },
              width: '100%',
            }}
          >
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Typography 
                variant="h3" 
                component="h2" 
                color="primary.main"
                sx={{
                  fontWeight: 600,
                  position: 'relative',
                  display: 'inline-block',
                  mb: 4,
                  fontSize: { xs: '2rem', md: '2.5rem' },
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: -16,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: 100,
                    height: 4,
                    backgroundColor: 'secondary.main',
                  }
                }}
              >
                Experience & Credentials
              </Typography>
            </Box>
            <Grid container spacing={4} justifyContent="center">
              {achievements.map((achievement, index) => (
                <Grid item xs={6} sm={3} key={index}>
                  <Box 
                    sx={{ 
                      textAlign: 'center',
                    }}
                  >
                    {achievement.icon}
                    <Typography 
                      variant="h3" 
                      sx={{ 
                        mt: 2, 
                        mb: 1, 
                        color: 'primary.main',
                        fontWeight: 700,
                        fontSize: { xs: '1.75rem', md: '2.5rem' }
                      }}
                    >
                      {achievement.number}
                    </Typography>
                    <Typography 
                      variant="body1" 
                      color="text.secondary"
                      sx={{
                        fontSize: { xs: '0.875rem', md: '1rem' }
                      }}
                    >
                      {achievement.label}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* Services Section */}
        <Box
          sx={{
            minHeight: '70vh',
            display: 'flex',
            alignItems: 'center',
            py: 6,
            width: '100%',
            background: 'linear-gradient(135deg, rgba(27,60,105,0.03) 0%, rgba(43,89,153,0.03) 100%)',
          }}
        >
          <Container 
            maxWidth={false}
            sx={{
              px: { xs: 2, sm: 3, md: 6, lg: 8 },
              width: '100%',
            }}
          >
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Typography 
                variant="h3" 
                component="h2" 
                color="primary.main"
                sx={{
                  fontWeight: 600,
                  position: 'relative',
                  display: 'inline-block',
                  mb: 4,
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: -16,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: 100,
                    height: 4,
                    backgroundColor: 'secondary.main',
                  }
                }}
              >
                Our Services
              </Typography>
              <Typography 
                variant="h6" 
                color="text.secondary" 
                sx={{ 
                  maxWidth: 800, 
                  mx: 'auto',
                  mb: 8,
                  fontWeight: 400
                }}
              >
                Help with tax, bookkeeping and business decisions, all in one place
              </Typography>
            </Box>
            
            <Box 
              sx={{ 
                py: 3,
                mx: { xs: 0, md: -2 },
                px: { xs: 0, md: 2 },
                overflow: { xs: 'auto', md: 'visible' },
                WebkitOverflowScrolling: 'touch',
              }}
            >
              <Grid 
                container 
                spacing={{ xs: 2, md: 4 }} 
                sx={{ 
                  display: 'flex',
                  flexDirection: { xs: 'column', lg: 'row' },
                  paddingTop: 4,
                  flexWrap: { xs: 'nowrap', lg: 'wrap' },
                  overflowY: 'visible',
                  '& > .MuiGridLegacy-item': {
                    flex: { xs: '0 0 100%', lg: '1 0 auto' },
                    width: { xs: '100%', lg: '33.333%' },
                    py: 2,
                  }
                }}
              >
                {services.slice(0, 3).map((service, index) => (
                  <Grid item key={index}>
                    <Paper 
                      elevation={0}
                      sx={{ 
                        p: { xs: 3, md: 4 },
                        height: '100%',
                        textAlign: 'center',
                        transition: 'all 0.2s ease-in-out',
                        transform: 'translateY(0)',
                        border: '1px solid',
                        borderColor: 'divider',
                        minWidth: { xs: '100%', lg: '300px' },
                        position: 'relative',
                        zIndex: 1,
                        '&:hover': {
                          transform: 'translateY(-8px)',
                          boxShadow: (theme) => theme.shadows[4],
                          zIndex: 2,
                        },
                        willChange: 'transform',
                      }}
                    >
                      {service.icon}
                      <Typography 
                        variant="h5" 
                        component="h3" 
                        gutterBottom 
                        color="primary.main"
                        sx={{
                          fontSize: { xs: '1.25rem', md: '1.5rem' }
                        }}
                      >
                        {service.title}
                      </Typography>
                      <Typography 
                        color="text.secondary"
                        sx={{
                          fontSize: { xs: '0.875rem', md: '1rem' }
                        }}
                      >
                        {service.description}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Container>
        </Box>

        {/* Reviews Section */}
        <Box 
          sx={{ 
            minHeight: '60vh',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            py: 6,
            background: 'white',
          }}
        >
          <Container maxWidth="lg" sx={{ mb: 6 }}>
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Typography 
                variant="h3" 
                component="h2" 
                color="primary.main"
                sx={{
                  fontWeight: 600,
                  position: 'relative',
                  display: 'inline-block',
                  mb: 4,
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: -16,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: 100,
                    height: 4,
                    backgroundColor: 'secondary.main',
                  }
                }}
              >
                Client Success Stories
              </Typography>
              <Typography 
                variant="h6" 
                color="text.secondary"
                sx={{ 
                  maxWidth: 800, 
                  mx: 'auto',
                  fontWeight: 400
                }}
              >
                What clients say about working with Good Shepherd
              </Typography>
            </Box>
          </Container>
          <Container maxWidth="md">
            <Box sx={{ textAlign: 'center' }}>
              <Rating value={featuredReview.rating} readOnly sx={{ mb: 3 }} />
              <Typography
                variant="h5"
                component="blockquote"
                sx={{
                  fontStyle: 'italic',
                  fontWeight: 400,
                  lineHeight: 1.6,
                  color: 'text.primary',
                  mb: 3,
                }}
              >
                “{featuredReview.review}”
              </Typography>
              <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 4 }}>
                {featuredReview.name}, {featuredReview.source}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  color="primary"
                  href={GOOGLE_PROFILE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Read our reviews on Google
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  href={GOOGLE_WRITE_REVIEW_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Leave us a review
                </Button>
              </Box>
            </Box>
          </Container>
        </Box>

        {/* Why Choose Us Section */}
        <Box 
          sx={{ 
            minHeight: '50vh',
            display: 'flex',
            alignItems: 'center',
            py: 6,
            background: 'linear-gradient(135deg, rgba(27,60,105,0.03) 0%, rgba(43,89,153,0.03) 100%)',
          }}
        >
          <Container maxWidth="lg">
            <Box sx={{ textAlign: 'center', maxWidth: 800, mx: 'auto' }}>
              <Typography 
                variant="h3" 
                component="h2" 
                color="primary.main"
                sx={{
                  fontWeight: 600,
                  position: 'relative',
                  display: 'inline-block',
                  mb: 4,
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: -16,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: 100,
                    height: 4,
                    backgroundColor: 'secondary.main',
                  }
                }}
              >
                Why Choose Us?
              </Typography>
              <Typography 
                variant="h6" 
                color="text.secondary" 
                sx={{ 
                  mb: 4,
                  fontWeight: 400
                }}
              >
                You deal directly with your accountant, someone who actually knows your business and picks up the phone.
              </Typography>
              <Button
                variant="contained"
                color="primary"
                size="large"
                component={RouterLink}
                to="/contact"
                sx={{ 
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                }}
              >
                Book Your Free Consultation
              </Button>
            </Box>
          </Container>
        </Box>
      </Box>

      <style>
        {`
          @keyframes float {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-10px); }
          }
        `}
      </style>
    </>
  );
};

export default Home;